import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Section } from '../components/ui/Section';
import { ActionLink } from '../components/ui/ActionLink';
import { TextLink } from '../components/ui/TextLink';
import { StepList } from '../components/ui/StepList';
import { TimetableTable } from '../components/ui/TimetableTable';
import { TestimonialList } from '../components/ui/TestimonialList';
import { ArticleCard } from '../components/ui/ArticleCard';
import { MockupTag } from '../components/ui/MockupTag';
import { HomeHero } from '../components/home/HomeHero';
import { AccreditationMarquee } from '../components/home/AccreditationMarquee';
import { DestinationShowcase } from '../components/home/DestinationShowcase';
import { AudienceFinder } from '../components/home/AudienceFinder';
import { WhyBento } from '../components/home/WhyBento';
import { EnquirySection } from '../components/forms/EnquirySection';
import { enrolmentSteps, communityLinks } from '../data/home';
import { testimonials } from '../data/people';
import { articles } from '../data/blog';
import { contact } from '../data/contact';
function CtaRow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1205" data-mp="2mxa84n96bogq">{children}</div>;
}
export function Home() {
  usePageMeta('English Speaking Course for Adults, Teens and Professionals | ES World', 'Learn English with ES World. Choose an English speaking course, exam class or camp. See prices and book a free lesson.');
  return <>
      <HomeHero data-id="element-1206" data-mp="285rjy265unma" />
      <AccreditationMarquee data-id="element-1207" data-mp="23912baepjjyr" />

      <Section id="where" bg="grey" title="Study all year or by season" description="Join a year-round campus and start any Monday, or choose a summer or winter programme. Every location uses the same teaching method and support." data-id="element-1208" data-mp="23a2h9pyugqlr">
        <DestinationShowcase data-id="element-1209" data-mp="1kkyyx40xbdo3" />
        <CtaRow data-id="element-1210" data-mp="1kjnur33kg3wg">
          <ActionLink to="#enquire" data-id="element-1211" data-mp="1kj5ttt5yl1i7">Ask about campus dates</ActionLink>
          <ActionLink to="/price-list/dubai" variant="secondary" data-id="element-1212" data-mp="1omkj7afseu8b">
            Check campus prices
          </ActionLink>
        </CtaRow>
      </Section>

      <Section id="courses" title="Find the course that fits you" description="Tell us who you are, and we show you the right courses. We have English courses for adults, university students, professionals, teens, young learners and teachers." data-id="element-1213" data-mp="1aovzi5xght4u">
        <AudienceFinder data-id="element-1214" data-mp="289rwt613vvlc" />
        <div className="mt-10 flex flex-col items-start gap-4 rounded-[24px] border border-line p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1215" data-mp="28c4erj6v65ye">
          <p className="max-w-xl type-body" data-id="element-1216" data-mp="1yjgluzj08m4y">Not sure which course is right? Tell us your goal and we send you a clear plan and a sample price.</p>
          <ActionLink to="#enquire" data-id="element-1217" data-mp="2m89i6x3jgw03">Get your course price</ActionLink>
        </div>
      </Section>

      <Section id="why" bg="cream" title="Why learn English with us?" subhead="Learn with certified native teachers on a modern campus, with support in your language and a student community from all over the world." data-id="element-1218" data-mp="2r2m73mnsazj8">
        <WhyBento data-id="element-1219" data-mp="ocuctrx2avqu" />
      </Section>

      <Section id="how" title="Start in four steps" description="Booking a course is simple. Our team helps you at every step, from the level test to your first day in class." aside={<TextLink to="/about/teaching-methodology" data-id="element-1221" data-mp="3su6tm8aaxz0i">Read how our lessons work</TextLink>} data-id="element-1220" data-mp="og8ib910vl2i">
        <StepList items={enrolmentSteps} data-id="element-1222" data-mp="3nvlwihqcjvga" />
        <CtaRow data-id="element-1223" data-mp="5nwcudf2o3o5">
          <ActionLink to="#enquire" data-id="element-1224" data-mp="5ofpbelydzus">Take a free level test</ActionLink>
          <ActionLink to={contact.whatsappLink} variant="secondary" data-id="element-1225" data-mp="oa2cbqmp1j1">
            Ask us on WhatsApp
          </ActionLink>
        </CtaRow>
      </Section>

      <Section id="when" bg="grey" title="Start any Monday" description="Year-round courses run every week. Choose morning, midday or late afternoon classes, and 15 or 30 lessons a week, for one week or up to a full year." data-id="element-1226" data-mp="1h4w5ja5c0kip">
        <TimetableTable data-id="element-1227" data-mp="2cdrbos3gc2ix" />
        <CtaRow data-id="element-1228" data-mp="2cd81jxk1mifg">
          <ActionLink to="#enquire" data-id="element-1229" data-mp="2cesxnrm0a9ff">Reserve my start date</ActionLink>
          <ActionLink to="/price-list/london" variant="secondary" data-id="element-1230" data-mp="2hahtbjuwn66g">
            Compare price lists
          </ActionLink>
        </CtaRow>
      </Section>

      <Section id="community" title="Make friends from day one" description="Join trips, games and city walks with classmates from many countries. Live close to school in a room that we arrange for you." data-id="element-1231" data-mp="2qdainhkj17tn">
        <div className="grid gap-5 md:grid-cols-2" data-id="element-1232" data-mp="3032gqlru4j2v">
          {communityLinks.map(item => <Link key={item.to} to={item.to} className="group relative flex min-h-[380px] flex-col justify-end overflow-hidden rounded-[28px] p-6 md:p-8" data-id="element-1233" data-mp="350c5dz7egqpn">
              <img src={item.image} alt={item.alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-brand group-hover:scale-[1.03]" data-id="element-1234" data-mp="3e222nlnyqjvc" />
              <div className="absolute inset-0 bg-black/45" aria-hidden="true" data-id="element-1235" data-mp="ycle9lgr7r6r" />
              <MockupTag note="Placeholder image" className="absolute left-5 top-5" data-id="element-1236" data-mp="tfcx9o7cdt73" />
              <span className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-[15px] bg-white text-ink transition-colors duration-150 group-hover:bg-brand group-hover:text-white" aria-hidden="true" data-id="element-1237" data-mp="ofqi397ye2vn">
                <ArrowUpRightIcon className="h-5 w-5" data-id="element-1238" data-mp="1cd61quxm6b6l" />
              </span>
              <h3 className="relative type-image-title text-white" data-id="element-1239" data-mp="1cbbbr27hjsvn">{item.title}</h3>
              <p className="relative mt-2 max-w-sm type-card-desc text-white" data-id="element-1240" data-mp="17djm6eubcium">{item.text}</p>
            </Link>)}
        </div>
      </Section>

      <Section id="students" bg="cream" title="Words from our students" mockup="Sample testimonials" data-id="element-1241" data-mp="2030bh3mmzjmm">
        <TestimonialList items={testimonials.slice(0, 3)} data-id="element-1242" data-mp="3ua2ias65t7tf" />
        <CtaRow data-id="element-1243" data-mp="3ucp0dlqof5pw">
          <ActionLink to={contact.emailLink} data-id="element-1244" data-mp="26s6dhfmbu4q">Email our admissions team</ActionLink>
        </CtaRow>
      </Section>

      <Section id="blog" title="Tips to help you learn" description="Short, clear guides from our team. Read about choosing a course, studying abroad and preparing your child for camp." mockup="Sample articles" aside={<ActionLink to="/blog" variant="secondary" data-id="element-1246" data-mp="3aopp89s79wjy">
            Read all our guides
          </ActionLink>} data-id="element-1245" data-mp="3kl10qxjyuidc">
        <div className="grid gap-8 md:grid-cols-3" data-id="element-1247" data-mp="31lwyq8qudov6">
          {articles.map(article => <ArticleCard key={article.slug} article={article} data-id="element-1248" data-mp="36l02lpv35p1k" />)}
        </div>
      </Section>

      <EnquirySection bg="grey" title="Plan your English course today" description="Tell us your goal, your dates and where you want to study. Our admissions team replies with a course plan and a sample price." submitLabel="Send my free enquiry" data-id="element-1249" data-mp="zuk3kbeoevcb" />
    </>;
}